import { useState } from "react";
import { UploadSimple } from "@phosphor-icons/react";
import { api, act } from "@/lib/api";
import { Sel } from "@/components/Sel";
import { Badge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const ImportDialog = ({ open, onClose, onDone }) => {
  const [pv, setPv] = useState(null);
  const [map, setMap] = useState({});
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    const r = await act(() => api.post("/contacts/import/preview", fd));
    setBusy(false);
    if (r) { setPv(r); setMap(r.mapping); setRes(null); }
  };
  const commit = async () => {
    setBusy(true);
    const r = await act(() => api.post("/contacts/import/commit", { job_id: pv.job_id, mapping: map }), "Import complete");
    setBusy(false);
    if (r) { setRes(r); onDone(); }
  };
  const close = () => { setPv(null); setRes(null); onClose(); };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#121212] border-border max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Import contacts (CSV / XLSX)</DialogTitle></DialogHeader>
        {!pv && (
          <label className="border border-dashed border-border rounded-md p-10 flex flex-col items-center gap-3 cursor-pointer hover:border-[#007AFF] transition-colors" data-testid="import-dropzone">
            <UploadSimple size={28} className="text-[#007AFF]" />
            <span className="text-sm">{busy ? "Parsing…" : "Choose a .csv or .xlsx file"}</span>
            <span className="text-[11px] text-zinc-500">Optional columns: consent (yes) + consent_evidence, opted_out (yes). Re-import never restores an opt-out.</span>
            <input type="file" accept=".csv,.xlsx" hidden data-testid="import-file-input" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          </label>
        )}
        {pv && !res && (
          <div className="space-y-4">
            <div className="text-sm text-zinc-400">{pv.total} rows detected. Map columns:</div>
            <div className="grid grid-cols-2 gap-2">{pv.headers.map((h) => (
              <div key={h} className="flex items-center gap-2">
                <span className="text-xs font-mono w-36 truncate">{h}</span>
                <Sel testid={`map-${h.replace(/\W+/g, "-")}`} value={map[h] || "__skip"} onChange={(v) => setMap({ ...map, [h]: v === "__skip" ? "" : v })}
                  options={[{ value: "__skip", label: "— skip —" }, ...pv.fields.map((f) => ({ value: f, label: f }))]} />
              </div>
            ))}</div>
            <div className="overflow-x-auto border border-border rounded-md">
              <table className="text-xs w-full"><thead><tr>{pv.headers.map((h) => <th key={h} className="text-left p-2 border-b border-border text-zinc-400">{h}</th>)}</tr></thead>
                <tbody>{pv.sample.map((r, i) => <tr key={i}>{pv.headers.map((h) => <td key={h} className="p-2 border-b border-border/50">{r[h]}</td>)}</tr>)}</tbody></table>
            </div>
            <div className="flex justify-end"><Button data-testid="import-commit-btn" disabled={busy} onClick={commit} className="bg-[#007AFF] hover:bg-[#3395FF]">{busy ? "Importing…" : `Import ${pv.total} rows`}</Button></div>
          </div>
        )}
        {res && (
          <div className="space-y-3" data-testid="import-result">
            <div className="flex flex-wrap gap-2">
              <Badge v={`${res.created} created`} tone="green" /><Badge v={`${res.updated} updated`} tone="blue" /><Badge v={`${res.duplicates_merged} duplicates merged`} />
              <Badge v={`${res.suppressed} suppressed`} tone="red" /><Badge v={`${res.held} held for review`} tone="amber" /><Badge v={`${res.errors.length} row errors`} tone={res.errors.length ? "red" : "gray"} />
            </div>
            {res.errors.length > 0 && <ul className="text-xs font-mono text-[#FF6961] space-y-1" data-testid="import-row-errors">{res.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}</ul>}
            <div className="flex justify-end"><Button variant="secondary" onClick={close} data-testid="import-close-btn">Done</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
